import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Wrench, Facebook, Instagram, Music, MessageCircle, ArrowUp } from "lucide-react";
import { useInView } from "@/hooks";
import { cn } from "@/lib/utils";

const footerLinks = {
  navigation: [
    { label: "Accueil", href: "#" },
    { label: "À propos", href: "#about" },
    { label: "Comment ça marche", href: "#how-it-works" },
    { label: "Nos artisans", href: "#services" },
    { label: "FAQ", href: "#faq" },
    { label: "Contact", href: "#contact" },
  ],
  artisans: [
    { label: "Devenir artisan", href: "#contact" },
    { label: "Connexion artisan", href: "#" },
    { label: "Guide artisan", href: "#" },
    { label: "Charte qualité", href: "#" },
  ],
  contact: [
    { label: "+225 01 42 20 46 01", href: "tel:+2250142204601" },
    { label: "alloartisan20@gmail.com", href: "mailto:alloartisan20@gmail.com" },
  ],
};

const socialLinks = [
  { icon: Facebook, href: "https://facebook.com/AlloArtisanCI", label: "Facebook" },
  { icon: Instagram, href: "https://instagram.com/AlloArtisan_ci", label: "Instagram" },
  { icon: Music, href: "https://tiktok.com/@AlloArtisan", label: "TikTok" },
  { icon: MessageCircle, href: "https://wa.me/2250142204601", label: "WhatsApp" },
];

export function Footer() {
  const { ref, isInView } = useInView();

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <footer ref={ref} className="bg-dark text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12"
        >
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                <Wrench className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl">Allo Artisan</span>
            </div>
            <p className="text-white/60 text-sm leading-relaxed">
              L'artisan de confiance, À portée de main. Connectez-vous avec des artisans qualifiés et vérifiés à Abidjan.
            </p>
            <div className="flex items-center gap-3 pt-2">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center hover:bg-primary transition-colors"
                  aria-label={social.label}
                >
                  <social.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-semibold mb-4 text-white/90">Navigation</h4>
            <ul className="space-y-2.5">
              {footerLinks.navigation.map((link) => (
                <li key={link.label}>
                  <button
                    onClick={() => {
                      if (link.href === "#") {
                        scrollToTop();
                      } else {
                        const el = document.querySelector(link.href);
                        if (el) {
                          const top = el.getBoundingClientRect().top + window.scrollY - 80;
                          window.scrollTo({ top, behavior: "smooth" });
                        }
                      }
                    }}
                    className="text-white/50 hover:text-primary transition-colors text-sm"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Artisans */}
          <div>
            <h4 className="font-semibold mb-4 text-white/90">Artisans</h4>
            <ul className="space-y-2.5">
              {footerLinks.artisans.map((link) => (
                <li key={link.label}>
                  <button
                    onClick={() => {
                      const el = document.querySelector(link.href);
                      if (el) {
                        const top = el.getBoundingClientRect().top + window.scrollY - 80;
                        window.scrollTo({ top, behavior: "smooth" });
                      }
                    }}
                    className="text-white/50 hover:text-primary transition-colors text-sm"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold mb-4 text-white/90">Contact</h4>
            <ul className="space-y-2.5">
              {footerLinks.contact.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="text-white/50 hover:text-primary transition-colors text-sm"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-white/40 text-xs">
              Abidjan, Côte d'Ivoire
            </p>
          </div>
        </motion.div>

        {/* Bottom */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4"
        >
          <p className="text-white/40 text-sm text-center sm:text-left">
             2026 Allo Artisan © Tous droits réservés.
          </p>
          <div className="flex items-center gap-4 text-sm">
            <Link to="#" className="text-white/40 hover:text-primary transition-colors">Politique de confidentialité</Link>
            <span className="text-white/20">|</span>
            <Link to="#" className="text-white/40 hover:text-primary transition-colors">Conditions d'utilisation</Link>
          </div>
        </motion.div>
      </div>

      {/* Scroll to top button */}
      <button
        onClick={scrollToTop}
        className="fixed bottom-6 right-6 w-12 h-12 bg-primary text-white rounded-full shadow-lg hover:bg-primary-dark transition-all hover:scale-110 z-30 flex items-center justify-center"
        aria-label="Retour en haut"
      >
        <ArrowUp className="w-5 h-5" />
      </button>
    </footer>
  );
}
